import { ColumnDef } from '@tanstack/react-table';
import { SupplierStatus } from '../suppliers/SupplierStatus';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import SupplierStatusCell from '@/components/suppliers/SupplierStatusCell';
import { CircuitResponse } from './CircuitResponse';
import { Translator } from '../translator.type';

export type Circuit = {
  id: string;
  code: number;
  name: string;
  remarks?: string;
  countries: string[];
  durationDays: number;
  durationNights: number;
  status: SupplierStatus;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    firstname: string;
    lastname: string;
  };
  lastUpdatedBy?: {
    firstname: string;
    lastname: string;
  };
  _count: {
    sharedCircuits: number;
  };
};

export const circuitColumns = (t: Translator): ColumnDef<Circuit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'code',
    header: t('circuits.columns.code'),
    cell: ({ row }) =>
      row.original.code && (
        <span>{`C${row.original.code.toString().padStart(4, '0')}`}</span>
      ),
  },
  {
    accessorKey: 'name',
    header: t('circuits.columns.circuit'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'remarks',
    header: t('circuits.columns.remarks'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="remarques"
          >
            {row.original.remarks || '-'}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="remarques completes"
          >
            {row.original.remarks || '-'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'countries',
    header: t('circuits.columns.country'),
    cell: ({ row }) => <span>{row.original.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'durationDays',
    header: t('circuits.columns.duration'),
    cell: ({ row }) =>
      row.original.durationDays ? (
        <span>
          {row.original.durationDays}j
          {row.original.durationNights && ` / ${row.original.durationNights}n`}
        </span>
      ) : null,
  },
  {
    accessorKey: 'updatedAt',
    header: t('circuits.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'lastUpdatedBy',
    header: t('circuits.columns.lastUpdatedBy'),
    cell: ({ row }) => {
      return row.original.lastUpdatedBy ? (
        <span>
          {row.original.lastUpdatedBy.firstname}{' '}
          {row.original.lastUpdatedBy.lastname}
        </span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    accessorKey: '_count.sharedCircuits',
    header: t('circuits.columns.distributedBy'),
  },
  {
    accessorKey: 'status',
    header: t('circuits.columns.status'),
    cell: ({ row }) => <SupplierStatusCell status={row.original.status} />,
  },
];

export type DistributorCircuit = Omit<Circuit, '_count' | 'status'> & {
  company: {
    id: string;
    name: string;
  };
};

export const distributorCircuitColumns = (
  t: Translator,
): ColumnDef<DistributorCircuit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'code',
    header: t('distributorCircuits.list.undistributedCircuits.columns.code'),
    cell: ({ row }) =>
      row.original.code && (
        <span>{`C${row.original.code.toString().padStart(4, '0')}`}</span>
      ),
  },
  {
    accessorKey: 'name',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.circuitName',
    ),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'company.name',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.receiver',
    ),
  },
  {
    accessorKey: 'countries',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.countries',
    ),
    cell: ({ row }) => <span>{row.original.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'durationDays',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.duration',
    ),
    cell: ({ row }) =>
      row.original.durationDays ? (
        <span>
          {row.original.durationDays}j
          {row.original.durationNights && ` / ${row.original.durationNights}n`}
        </span>
      ) : null,
  },
  {
    accessorKey: 'createdAt',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.createdAt',
    ),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.createdAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.updatedAt',
    ),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
];

export type DistributedCircuit = {
  circuit: Omit<Circuit, '_count' | 'status'> & {
    company: {
      id: string;
      name: string;
    };
  } & {
    marginRate?: number;
    circuitDays?: Omit<
      CircuitResponse['circuitDays'][number],
      'id' | 'circuitId' | 'stepTitle' | 'images' | 'description'
    >[];
  };
  distributedAt: string | null;
};

export const distributedCircuitColumns = (
  t: Translator,
): ColumnDef<DistributedCircuit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'circuit.code',
    header: t('distributorCircuits.list.columns.code'),
    cell: ({ row }) =>
      row.original.circuit.code && (
        <span>{`C${row.original.circuit.code.toString().padStart(4, '0')}`}</span>
      ),
  },
  {
    accessorKey: 'circuit.name',
    header: t('distributorCircuits.list.columns.circuitName'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.circuit.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.circuit.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'circuit.company.name',
    header: t('distributorCircuits.list.columns.receiver'),
  },
  {
    accessorKey: 'circuit.countries',
    header: t('distributorCircuits.list.columns.countries'),
    cell: ({ row }) => <span>{row.original.circuit.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'circuit.durationDays',
    header: t('distributorCircuits.list.columns.duration'),
    cell: ({ row }) =>
      row.original.circuit.durationDays ? (
        <span>
          {row.original.circuit.durationDays}j
          {row.original.circuit.durationNights &&
            ` / ${row.original.circuit.durationNights}n`}
        </span>
      ) : null,
  },
  {
    accessorKey: 'distributedAt',
    header: t('distributorCircuits.list.columns.distributedAt'),
    cell: ({ row }) =>
      row.original.distributedAt ? (
        <span>
          {format(new Date(row.original.distributedAt), 'dd/MM/yyyy')}
        </span>
      ) : (
        <span>-</span>
      ),
  },
  {
    accessorKey: 'circuit.updatedAt',
    header: t('distributorCircuits.list.columns.updatedAt'),
    cell: ({ row }) => (
      <span>
        {format(new Date(row.original.circuit.updatedAt), 'dd/MM/yyyy')}
      </span>
    ),
  },
];
